import type { EcwIdeaReference } from '@citizen-walk/shared-types';
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * 気づき (insight) / 処方箋 (idea) の outbox (オフライン送信保留キュー、POC)。
 *
 * 旧 v1 では Firestore 永続キャッシュ (`persistentLocalCache`) のみで暗黙保留していたが、
 * v2 では posts/outbox.ts と同様の「明示的キュー + UI バッジ + リトライ」UX を提供する。
 *
 * Phase 5 POC:
 *   - text payload + 画像は base64 文字列で保存 (Storage upload は無いので Blob 分岐不要)
 *   - IndexedDB に永続化、tab 閉じても保持
 *   - online 復帰時 / 手動リトライで送信
 *
 * 写真 Blob 保存は posts/outbox.ts と異なり不要 — Idea の referenceImageBase64 は
 * 既に dataURL/http URL 文字列なので IDB native 保存で済む。
 */

const DB_NAME = 'citizen-walk-ecw-outbox';
const DB_VERSION = 1;
const STORE_INSIGHTS = 'insights';
const STORE_IDEAS = 'ideas';

/** insight 用 outbox entry (CreateInsightInput + 送信状態 + districtId)。 */
export interface OutboxInsight {
  /** auto-increment ID (IndexedDB primary key)。 */
  id?: number;
  /** 作成日時 (ローカル、送信時に serverTimestamp で上書き)。 */
  createdLocalAt: number;
  districtId: string;
  userId: string;
  nickname: string;
  // EcwInsight payload (CreateInsightInput と同じ shape)
  title: string;
  observations: string;
  diagnosis: string;
  sentiment: number;
  selectedPostIds: string[];
  initialThoughts?: string;
  llmAnalysis?: string;
  llmResolutionQuestions?: string[];
  llmDiagnosisQuestions?: string[];
  emotionTag?: string;
  categoryLabel?: string;
  team?: string;
  /** キャッチ画像 (キービジュアル、dataURL or http URL)。 */
  referenceImageBase64?: string;
  /** メインセッション ID (作成時の membership.mainSessionId、表示は名前解決)。 */
  sessionId?: string | null;
  /** 最後の送信試行のエラーメッセージ (UI 表示用、null なら未試行)。 */
  lastError: string | null;
  /** 試行回数 (UI / 自動 retry の最大回数判定用)。 */
  attempts: number;
  /**
   * 送信ステータス (posts/outbox.ts と同じセマンティクス、optional)。
   *   - 'pending' (default): 未送信 + 試行中、自動 retry 対象
   *   - 'failed': MAX_ATTEMPTS 超過、自動 retry skip (手動 flush では再試行)
   */
  status?: 'pending' | 'failed';
}

/** idea 用 outbox entry (CreateIdeaInput + 送信状態 + districtId)。 */
export interface OutboxIdea {
  /** auto-increment ID。 */
  id?: number;
  createdLocalAt: number;
  districtId: string;
  userId: string;
  nickname: string;
  title: string;
  observations: string;
  actionIdea: string;
  insightIds: string[];
  selectedPostIds: string[];
  references?: EcwIdeaReference[];
  referenceImageBase64?: string;
  llmActionQuestions?: string[];
  llmEffectQuestions?: string[];
  llmInsightSummary?: string;
  emotionTag?: string;
  emotionDescription?: string;
  categoryCode?: string;
  categoryLabel?: string;
  team?: string;
  realName?: string;
  /** メインセッション ID (作成時の membership.mainSessionId、表示は名前解決)。 */
  sessionId?: string | null;
  lastError: string | null;
  attempts: number;
  status?: 'pending' | 'failed';
}

/**
 * 自動 retry の最大試行回数。これ以上失敗したら status='failed' に遷移し、UI で
 * 別 badge 表示する (posts/outbox.ts と統一)。
 */
export const MAX_ATTEMPTS = 5;

interface EcwOutboxDB extends DBSchema {
  insights: {
    key: number;
    value: OutboxInsight;
  };
  ideas: {
    key: number;
    value: OutboxIdea;
  };
}

let dbPromise: Promise<IDBPDatabase<EcwOutboxDB>> | null = null;

function getDb(): Promise<IDBPDatabase<EcwOutboxDB>> {
  if (!dbPromise) {
    dbPromise = openDB<EcwOutboxDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_INSIGHTS)) {
          db.createObjectStore(STORE_INSIGHTS, { keyPath: 'id', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains(STORE_IDEAS)) {
          db.createObjectStore(STORE_IDEAS, { keyPath: 'id', autoIncrement: true });
        }
      },
    });
  }
  return dbPromise;
}

/**
 * BroadcastChannel: 他 tab で ecw outbox 変化を通知 (posts/outbox.ts と同パターン)。
 */
const BROADCAST_CHANNEL_NAME = 'citizen-walk-ecw-outbox-changed';
let bc: BroadcastChannel | null = null;
function getBroadcastChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!bc) bc = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
  return bc;
}

function notifyOutboxChanged() {
  getBroadcastChannel()?.postMessage({ type: 'ecw-outbox-changed' });
}

/** useEcwOutbox から購読 (unmount 時 unsubscribe)。 */
export function subscribeEcwOutboxChanges(callback: () => void): () => void {
  const channel = getBroadcastChannel();
  if (!channel) return () => {};
  const handler = () => callback();
  channel.addEventListener('message', handler);
  return () => channel.removeEventListener('message', handler);
}

/* ===== insights store helpers ===== */

export async function addInsightToOutbox(item: Omit<OutboxInsight, 'id'>): Promise<number> {
  const db = await getDb();
  const id = (await db.add(STORE_INSIGHTS, item)) as number;
  notifyOutboxChanged();
  return id;
}

export async function listInsightOutbox(): Promise<OutboxInsight[]> {
  const db = await getDb();
  const all = (await db.getAll(STORE_INSIGHTS)) as OutboxInsight[];
  return all.sort((a, b) => a.createdLocalAt - b.createdLocalAt);
}

export async function getInsightOutboxItem(id: number): Promise<OutboxInsight | undefined> {
  const db = await getDb();
  return (await db.get(STORE_INSIGHTS, id)) as OutboxInsight | undefined;
}

export async function deleteInsightFromOutbox(id: number): Promise<void> {
  const db = await getDb();
  await db.delete(STORE_INSIGHTS, id);
  notifyOutboxChanged();
}

export async function updateInsightOutboxItem(item: OutboxInsight): Promise<void> {
  const db = await getDb();
  await db.put(STORE_INSIGHTS, item);
  notifyOutboxChanged();
}

/* ===== ideas store helpers ===== */

export async function addIdeaToOutbox(item: Omit<OutboxIdea, 'id'>): Promise<number> {
  const db = await getDb();
  const id = (await db.add(STORE_IDEAS, item)) as number;
  notifyOutboxChanged();
  return id;
}

export async function listIdeaOutbox(): Promise<OutboxIdea[]> {
  const db = await getDb();
  const all = (await db.getAll(STORE_IDEAS)) as OutboxIdea[];
  return all.sort((a, b) => a.createdLocalAt - b.createdLocalAt);
}

export async function getIdeaOutboxItem(id: number): Promise<OutboxIdea | undefined> {
  const db = await getDb();
  return (await db.get(STORE_IDEAS, id)) as OutboxIdea | undefined;
}

export async function deleteIdeaFromOutbox(id: number): Promise<void> {
  const db = await getDb();
  await db.delete(STORE_IDEAS, id);
  notifyOutboxChanged();
}

export async function updateIdeaOutboxItem(item: OutboxIdea): Promise<void> {
  const db = await getDb();
  await db.put(STORE_IDEAS, item);
  notifyOutboxChanged();
}

/** ecw outbox 全クリア (test 用 + 緊急時の手動操作)。 */
export async function clearEcwOutbox(): Promise<void> {
  const db = await getDb();
  await db.clear(STORE_INSIGHTS);
  await db.clear(STORE_IDEAS);
  notifyOutboxChanged();
}

/**
 * test 用に internal state を reset (mock 切替時)。
 */
export function __resetEcwOutboxDb() {
  dbPromise = null;
  if (bc) {
    bc.close();
    bc = null;
  }
}
